import { type FC, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  useRequestVideoDownloadMutation,
  useVideoDownloadQuery,
  VideoDownloadQuery,
} from "@/partner/entities/contents";
import { VideoDownloadStatusType } from "@/types/graphql/partner.generated";

/**
 * Короткие ролики Cloudflare собирает за секунды и держит процент на нуле
 * почти до конца: первые 30 секунд опрашиваем раз в 2 секунды, чтобы
 * готовая ссылка появлялась без паузы, дальше как у статуса обработки
 * видео (EditVideoDialog), раз в 5 секунд.
 */
const FAST_POLL_INTERVAL_MS = 2000;
const FAST_POLL_WINDOW_MS = 30_000;
const POLL_INTERVAL_MS = 5000;
// Код причины unavailable от бэкенда: Cloudflare не собирает MP4 для записи
// эфира длиннее 4 часов (CloudflareVideoProvider). Переводится, а не выводится
// как есть.
const DURATION_TOO_LONG = "DURATION_TOO_LONG";

export interface VideoDownloadProps {
  contentId: string;
}

/**
 * Кнопка «Скачать» в блоке шаринга диалога видео (как Download в
 * Cloudflare). Нажатие заказывает MP4 у провайдера, дальше поле
 * Content.videoDownload опрашивается, пока PREPARING; READY превращает
 * кнопку в ссылку на файл. Если файл уже готов при открытии, ссылка видна
 * сразу. См. docs/features/video-download.md.
 */
export const VideoDownload: FC<VideoDownloadProps> = ({ contentId }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { data, loading, startPolling, stopPolling, refetch } =
    useVideoDownloadQuery({
      variables: { id: contentId },
      // Подписанная ссылка живёт ограниченное время: при повторном открытии
      // диалога берём свежую, а не из кеша.
      fetchPolicy: "cache-and-network",
    });
  const download = data?.content?.videoDownload ?? null;
  const status = download?.status ?? null;

  const isPreparing = status === VideoDownloadStatusType.PreparingType;
  const [fastPolling, setFastPolling] = useState(true);
  const preparingSinceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isPreparing) {
      preparingSinceRef.current = null;
      setFastPolling(true);
      return undefined;
    }
    preparingSinceRef.current ??= Date.now();
    const left = FAST_POLL_WINDOW_MS - (Date.now() - preparingSinceRef.current);
    if (left <= 0) {
      setFastPolling(false);
      return undefined;
    }
    const timer = setTimeout(() => setFastPolling(false), left);
    return () => clearTimeout(timer);
  }, [isPreparing]);

  useEffect(() => {
    if (!isPreparing) return undefined;
    startPolling(fastPolling ? FAST_POLL_INTERVAL_MS : POLL_INTERVAL_MS);
    return () => stopPolling();
  }, [isPreparing, fastPolling, startPolling, stopPolling]);

  const [requestDownload, { loading: requesting }] =
    useRequestVideoDownloadMutation({
      variables: { contentId },
      // У VideoDownload нет id, Apollo сам его в кеш не положит: пишем
      // результат мутации в запрос статуса, чтобы кнопка переключилась без
      // лишнего запроса, а поллинг стартовал с него.
      update: (cache, { data: result }) => {
        if (!result) return;
        cache.writeQuery({
          query: VideoDownloadQuery,
          variables: { id: contentId },
          data: {
            content: {
              __typename: "Content",
              id: contentId,
              videoDownload: result.requestVideoDownload,
            },
          },
        });
      },
      onError: (error) => {
        enqueueSnackbar(error.message || t("videos.download.requestFailed"), {
          variant: "error",
        });
      },
    });

  const handleRequest = () => {
    void requestDownload();
  };

  if (!download) {
    // Видео ещё не готово (поле null) или первый запрос ещё идёт: кнопка
    // есть, но нажать нельзя, чтобы блок не прыгал.
    if (!loading) return null;
    return (
      <Button variant="outlined" startIcon={<DownloadIcon />} disabled>
        {t("videos.download.button")}
      </Button>
    );
  }

  switch (download.status) {
    case VideoDownloadStatusType.ReadyType:
      return (
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          href={download.url ?? undefined}
          target="_blank"
          rel="noopener"
          download
        >
          {t("videos.download.ready")}
        </Button>
      );

    case VideoDownloadStatusType.PreparingType: {
      // Ноль у Cloudflare значит «ещё не знаю», а не «ничего не сделано»:
      // тогда крутим без цифры, процент показываем, только когда он есть.
      const percent =
        typeof download.percent === "number" && download.percent > 0
          ? Math.round(download.percent)
          : null;
      return (
        <Stack spacing={1}>
          <Button
            variant="outlined"
            disabled
            startIcon={
              <CircularProgress
                size={16}
                color="inherit"
                variant={percent === null ? "indeterminate" : "determinate"}
                value={percent ?? undefined}
              />
            }
          >
            {percent === null
              ? t("videos.download.preparing")
              : t("videos.download.preparingPercent", { percent })}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {t("videos.download.preparingHint")}
          </Typography>
        </Stack>
      );
    }

    case VideoDownloadStatusType.ErrorType:
      return (
        <Alert
          severity="error"
          variant="outlined"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleRequest}
              disabled={requesting}
            >
              {t("videos.download.retry")}
            </Button>
          }
        >
          {t("videos.download.error")}
          {download.error && (
            <Typography variant="caption" component="div">
              {download.error}
            </Typography>
          )}
        </Alert>
      );

    case VideoDownloadStatusType.UnavailableType:
      return (
        <Alert
          severity="info"
          variant="outlined"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              {t("videos.download.refresh")}
            </Button>
          }
        >
          {download.error === DURATION_TOO_LONG
            ? t("videos.download.tooLong")
            : t("videos.download.unavailable")}
          {download.error && download.error !== DURATION_TOO_LONG && (
            <Typography variant="caption" component="div">
              {download.error}
            </Typography>
          )}
        </Alert>
      );

    case VideoDownloadStatusType.NotRequestedType:
    default:
      return (
        <Button
          variant="outlined"
          startIcon={
            requesting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DownloadIcon />
            )
          }
          onClick={handleRequest}
          disabled={requesting}
        >
          {requesting
            ? t("videos.download.preparing")
            : t("videos.download.button")}
        </Button>
      );
  }
};
